import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, MapPin, Package, Truck, Loader2 } from "lucide-react";
import { Button } from "@/app/components/button";
import type { CartItem } from "./cart-page";
import type { Address } from "./checkout-address-page";
import type { PaymentMethod } from "./checkout-payment-page";
import type { ShippingOption, ShippingRouteLabels } from "./checkout-shipping-page";
import { CheckoutProgress } from "@/app/components/checkout-progress";
import { formatCurrency, formatCEP } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { getAdImage } from "@/app/utils/ad-utils";
import {
  getOrder,
  getAdById,
  apiAdToAd,
  getShippingOptions,
  validateCheckout,
  getAccessToken,
  type OrderDetailApi,
  type ShippingOptionForCheckout,
  type CartItemApi,
  type CheckoutValidateResponse,
} from "@/app/api/auth-client";

/** Coerce to string so we never render an object as React child (error #300). */
function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

/** Line item shape from order API (product_name, quantity, unit_price_cents, etc.). */
interface OrderLineItemApi {
  ad_id?: string;
  product_name?: string;
  quantity?: number;
  unit_price_cents?: number;
  line_total_cents?: number;
}

interface CheckoutReviewPageProps {
  onBack: () => void;
  cartItems?: CartItem[];
  selectedAddress?: Address;
  /** Optional: when using Stripe, payment is chosen on the next screen. */
  selectedPayment?: PaymentMethod | null;
  selectedShipping?: ShippingOption;
  selectedInstallments?: number;
  onConfirmOrder?: () => void;
  /** Error from create-order (validation or API). Shown above confirm button. */
  orderError?: string | null;
  /** True while create order + payment intent is in progress. */
  isConfirming?: boolean;
  /** When present (hub/swap), show trajeto + pernas na Forma de Envio (igual etapa de frete). */
  routeLabels?: ShippingRouteLabels | null;
  /** Pay existing order mode: when set, fetch order and show summary; confirm opens Stripe. */
  payOrderId?: string | null;
  onPayOrder?: (orderId: string) => void | Promise<void>;
}

export function CheckoutReviewPage({
  onBack,
  cartItems = [],
  selectedAddress,
  selectedPayment = null,
  selectedShipping,
  selectedInstallments = 1,
  onConfirmOrder,
  orderError,
  isConfirming = false,
  routeLabels = null,
  payOrderId = null,
  onPayOrder,
}: CheckoutReviewPageProps) {
  const isPayOrderMode = !!payOrderId && !!onPayOrder;
  const [order, setOrder] = useState<OrderDetailApi | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderErrorLocal, setOrderErrorLocal] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [lineItemImages, setLineItemImages] = useState<Record<string, string>>({});
  /** Resolved shipping option label for pay-order mode (name, estimated days) so we show more than just the amount. */
  const [resolvedShippingOption, setResolvedShippingOption] = useState<ShippingOptionForCheckout | null>(null);
  /** P3/P4: server validate; null platform fee = loading or error. */
  const [serverPlatformFeeCents, setServerPlatformFeeCents] = useState<number | null>(null);
  const [validatePricingError, setValidatePricingError] = useState<string | null>(null);
  const [validateSnapshot, setValidateSnapshot] = useState<CheckoutValidateResponse | null>(null);

  useEffect(() => {
    if (!isPayOrderMode || !payOrderId) return;
    setOrderLoading(true);
    setOrderErrorLocal(null);
    setResolvedShippingOption(null);
    getOrder(payOrderId)
      .then(setOrder)
      .catch((e) => setOrderErrorLocal(safeText((e as Error)?.message) || "Não foi possível carregar o pedido."))
      .finally(() => setOrderLoading(false));
  }, [isPayOrderMode, payOrderId]);

  // Resolve shipping option label for pay-order mode (name, estimated days) so "Forma de Envio" matches the shipping step.
  useEffect(() => {
    if (!order?.shipping_option_id || !order?.address_snapshot || !Array.isArray(order.line_items) || order.line_items.length === 0) return;
    const addr = order.address_snapshot;
    const cep = (addr?.cep ?? "").replace(/\D/g, "").slice(0, 8);
    if (cep.length < 8) return;
    const items = (order.line_items as OrderLineItemApi[])
      .filter((i) => i.ad_id)
      .map((i) => ({ ad_id: i.ad_id!, quantity: Math.max(1, Number(i.quantity) || 1) }));
    if (items.length === 0) return;
    const recipientName = (addr?.recipient_name ?? "").trim();
    const recipientPhone = (addr?.recipient_phone ?? "").trim();
    getShippingOptions(cep, items, { recipient_name: recipientName, recipient_phone: recipientPhone })
      .then((options) => {
        const match = options.find((o) => o.id === order.shipping_option_id);
        setResolvedShippingOption(match ?? null);
      })
      .catch(() => setResolvedShippingOption(null));
  }, [order?.shipping_option_id, order?.address_snapshot, order?.line_items]);

  // Fetch ad images for pay-order line items (match regular checkout layout)
  useEffect(() => {
    if (!order?.line_items || !Array.isArray(order.line_items)) return;
    const items = order.line_items as OrderLineItemApi[];
    const adIds = [...new Set(items.map((i) => i.ad_id).filter(Boolean))] as string[];
    adIds.forEach((adId) => {
      getAdById(adId)
        .then((api) => {
          const ad = apiAdToAd(api);
          setLineItemImages((prev) => ({ ...prev, [adId]: getAdImage(ad) || "" }));
        })
        .catch(() => {});
    });
  }, [order?.line_items]);

  // P3/P4: authoritative fees for cart review (POST /auth/me/checkout/validate).
  useEffect(() => {
    if (isPayOrderMode || cartItems.length === 0) {
      setServerPlatformFeeCents(null);
      setValidatePricingError(null);
      setValidateSnapshot(null);
      return;
    }
    if (!getAccessToken()) {
      setServerPlatformFeeCents(0);
      setValidatePricingError(null);
      setValidateSnapshot(null);
      return;
    }
    const items: CartItemApi[] = cartItems.map((it) => ({
      ad_id: it.ad.id,
      quantity: Math.max(1, Number(it.quantity) || 1),
      ...(it.offerId ? { offer_id: it.offerId } : {}),
      ...(it.offerPrice != null && Number(it.offerPrice) > 0
        ? { agreed_price_cents: Math.round(Number(it.offerPrice) * 100) }
        : {}),
    }));
    const isSwapCart = cartItems.some((it) => it.isOfferItem === true);
    const shippingCents =
      selectedShipping != null ? Math.round(Number(selectedShipping.price) * 100) : 0;
    const secondPartyCents =
      isSwapCart && selectedShipping != null
        ? Math.round((selectedShipping.sellerAmount ?? 0) * 100)
        : 0;
    let cancelled = false;
    setValidatePricingError(null);
    validateCheckout(items, {
      shipping_cents: shippingCents,
      second_party_shipping_cents: secondPartyCents,
    })
      .then((res) => {
        if (!cancelled) {
          setServerPlatformFeeCents(res.platform_fee_cents ?? 0);
          setValidateSnapshot(res);
        }
      })
      .catch((e: Error & { status?: number; message?: string }) => {
        if (!cancelled) {
          setServerPlatformFeeCents(null);
          setValidateSnapshot(null);
          setValidatePricingError(e?.message ?? "Não foi possível calcular taxas.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isPayOrderMode, cartItems, selectedShipping]);

  // Price in reais for a cart item (used in cart-mode useMemo). Define once so hook order is fixed.
  const getItemPriceReais = (item: CartItem): number => {
    const offer = Number(item.offerPrice);
    if (offer > 0) return offer;
    const adPrice = Number(item.ad?.price);
    if (adPrice > 0) return adPrice;
    const cents = Number((item.ad as { price_cents?: number })?.price_cents);
    if (cents > 0) return cents / 100;
    return 0;
  };
  // All hooks must run on every render (React rules of hooks). No early return before useMemo.
  const cartTotals = useMemo(() => {
    const subtotal = cartItems.reduce((sum, item) => sum + getItemPriceReais(item) * (Number(item.quantity) || 1), 0);
    const shipping = selectedShipping?.price || 0;
    const platformFeeReais = (serverPlatformFeeCents ?? 0) / 100;
    const isSwapDeal = (validateSnapshot?.deal_type ?? "").trim().toLowerCase() === "swap";
    const buyerFirstCents = validateSnapshot?.buyer_first_payment_cents;
    const baseTotal =
      isSwapDeal && buyerFirstCents != null && buyerFirstCents >= 0
        ? buyerFirstCents / 100
        : subtotal + shipping + platformFeeReais;
    let totalWithInterest = baseTotal;
    let hasInterest = false;
    let interestRate = 0;
    const installments = selectedPayment?.type === "credit" ? selectedInstallments : 1;
    const installmentAmount = baseTotal / installments;
    if (selectedPayment?.type === "credit" && selectedInstallments > 3) {
      interestRate = (selectedInstallments - 3) * 0.015;
      totalWithInterest = baseTotal * (1 + interestRate);
      hasInterest = true;
    }
    return {
      subtotal,
      shipping,
      platformFeeReais,
      total: baseTotal,
      installmentAmount: hasInterest ? totalWithInterest / selectedInstallments : installmentAmount,
      totalWithInterest,
      hasInterest,
      interestRate,
    };
  }, [
    cartItems,
    selectedShipping,
    selectedPayment,
    selectedInstallments,
    serverPlatformFeeCents,
    validateSnapshot,
  ]);

  // Pay-order mode: render summary from order and "Concluir pagamento" → Stripe
  if (isPayOrderMode) {
    if (orderLoading) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
        </div>
      );
    }
    if (orderErrorLocal || !order) {
      return (
        <div className="min-h-screen bg-background pb-8">
          <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <h1 className="text-xl text-white">Revisão do Pedido</h1>
            </div>
          </header>
          <div className="max-w-7xl mx-auto px-4 py-8 text-center">
            <p className="text-muted-foreground">{safeText(orderErrorLocal) || "Pedido não encontrado."}</p>
            <Button onClick={onBack} className="mt-4">Voltar</Button>
          </div>
        </div>
      );
    }
    const lineItems = Array.isArray(order.line_items) ? (order.line_items as OrderLineItemApi[]) : [];
    const addr = order.address_snapshot;
    const freightCents = order.shipping_cents ?? 0;
    const freight = freightCents;
    const platformFeeCentsPay = order.platform_fee_cents ?? 0;
    const orderIsSwap = (order.deal_type ?? "").trim().toLowerCase() === "swap";
    const buyerHandlingPay = order.buyer_handling_cents ?? 0;
    return (
      <div className="min-h-screen bg-background pb-8">
        <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <div>
                <h1 className="text-xl text-white">Revisão do Pedido</h1>
                <p className="text-sm text-white/80">Confira os detalhes</p>
              </div>
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <CheckoutProgress currentStep={3} stepLabel="Revisão" totalSteps={3} />
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Endereço de Entrega</h3>
              </div>
              <div className="pl-7">
                <p className="font-medium">{addr && typeof addr.recipient_name === "string" ? addr.recipient_name : "Destinatário"}</p>
                <p className="text-sm text-muted-foreground">
                  {String(addr?.street ?? "")}, {String(addr?.number ?? "")}
                  {addr?.complement ? `, ${String(addr.complement)}` : ""}
                </p>
                <p className="text-sm text-muted-foreground">
                  {String(addr?.neighborhood ?? "")}, {String(addr?.city ?? "")} - {String(addr?.state ?? "")}
                </p>
                <p className="text-sm text-muted-foreground">CEP: {formatCEP(String(addr?.cep ?? ""))}</p>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Itens do Pedido</h3>
                <span className="text-sm text-muted-foreground">
                  ({lineItems.length} {lineItems.length === 1 ? "item" : "itens"})
                </span>
              </div>
              <div className="space-y-4 pl-7">
                {lineItems.map((item, i) => (
                  <div key={item.ad_id ?? i} className="flex gap-3">
                    <AdImage
                      src={lineItemImages[item.ad_id ?? ""] || ""}
                      alt={typeof item.product_name === "string" ? item.product_name : "Produto"}
                      className="w-16 h-16 object-cover rounded-lg shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">{typeof item.product_name === "string" ? item.product_name : "Produto"}</p>
                      <p className="text-xs text-muted-foreground">Quantidade: {safeText(item.quantity ?? 1)}</p>
                      <p className="text-sm font-semibold text-primary">
                        {formatCurrency(
                          (item.line_total_cents != null && item.line_total_cents > 0
                            ? item.line_total_cents
                            : (Number(item.unit_price_cents) || 0) * (Number(item.quantity) || 1)) / 100
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Forma de Envio</h3>
              </div>
              <div className="pl-7">
                {resolvedShippingOption ? (
                  <>
                    <p className="font-medium">{safeText(resolvedShippingOption.name)}</p>
                    <p className="text-sm text-muted-foreground">{safeText(resolvedShippingOption.estimatedDays)}</p>
                    <p className="text-sm font-semibold text-primary mt-1">{formatCurrency(Number.isFinite(freight) ? freight / 100 : 0)}</p>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-primary">{formatCurrency(Number.isFinite(freight) ? freight / 100 : 0)}</p>
                )}
              </div>
            </div>
            <div className="bg-card border-2 border-primary/20 rounded-lg p-6 space-y-4 shadow-md">
              <h2 className="font-semibold text-lg mb-4 text-primary">Resumo do Pedido</h2>
              <div className="space-y-3">
                {!orderIsSwap && (
                  <div className="flex justify-between text-base">
                    <span className="text-muted-foreground">Subtotal dos Produtos</span>
                    <span className="font-medium">{formatCurrency(Number(order.subtotal_cents) / 100 || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Frete</span>
                  <span className="font-medium">{formatCurrency(Number.isFinite(freight) ? freight / 100 : 0)}</span>
                </div>
                {platformFeeCentsPay > 0 && (
                  <div className="flex justify-between text-base">
                    <span className="text-muted-foreground">Taxa de Proteção e Validação.</span>
                    <span className="font-medium">{formatCurrency(platformFeeCentsPay / 100)}</span>
                  </div>
                )}
                {orderIsSwap && buyerHandlingPay > 0 && (
                  <div className="flex justify-between text-base">
                    <span className="text-muted-foreground">Serviços de Manuseio</span>
                    <span className="font-medium">{formatCurrency(buyerHandlingPay / 100)}</span>
                  </div>
                )}
                <div className="border-t-2 border-primary/20 pt-4 mt-4 flex justify-between items-center">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-2xl font-bold text-primary">{formatCurrency(Number(order.total_cents) / 100 || 0)}</span>
                </div>
              </div>
            </div>
            <Button
              className="w-full"
              size="lg"
              disabled={payLoading}
              onClick={async () => {
                if (!payOrderId || !onPayOrder) return;
                setPayLoading(true);
                try {
                  await onPayOrder(payOrderId);
                } catch (e) {
                  const msg = (e as Error)?.message;
                  setOrderErrorLocal(safeText(msg) || "Não foi possível abrir o pagamento.");
                } finally {
                  setPayLoading(false);
                }
              }}
            >
              {payLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Abrindo pagamento...
                </>
              ) : (
                "Concluir pagamento"
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Cart/checkout mode: require address + shipping (after all hooks).
  if (!selectedAddress || !selectedShipping) return null;

  const { subtotal, shipping, platformFeeReais, total, installmentAmount, totalWithInterest, hasInterest, interestRate } = cartTotals;
  const isSwapValidate = (validateSnapshot?.deal_type ?? "").trim().toLowerCase() === "swap";
  const cashDiffCents = validateSnapshot?.cash_difference_cents ?? 0;
  const buyerHandlingCents = validateSnapshot?.buyer_handling_cents ?? 0;

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Revisão do Pedido</h1>
              <p className="text-sm text-white/80">Confira os detalhes</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Indicator */}
        <CheckoutProgress currentStep={3} stepLabel="Revisão" totalSteps={3} />

        <div className="space-y-6">
          {/* Delivery Address */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Endereço de Entrega</h3>
            </div>
            <div className="pl-7">
              <p className="font-medium">{safeText(selectedAddress.name)}</p>
              <p className="text-sm text-muted-foreground">
                {safeText(selectedAddress.street)}, {safeText(selectedAddress.number)}
                {selectedAddress.complement != null && selectedAddress.complement !== "" ? `, ${safeText(selectedAddress.complement)}` : ""}
              </p>
              <p className="text-sm text-muted-foreground">
                {safeText(selectedAddress.neighborhood)}, {safeText(selectedAddress.city)} - {safeText(selectedAddress.state)}
              </p>
              <p className="text-sm text-muted-foreground">
                CEP: {formatCEP(safeText(selectedAddress.zipCode))}
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Itens do Pedido</h3>
              <span className="text-sm text-muted-foreground">
                ({cartItems.length} {cartItems.length === 1 ? "item" : "itens"})
              </span>
            </div>
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.ad.id} className="space-y-3">
                  <div className="flex gap-3 pl-7">
                    <AdImage
                      src={getAdImage(item.ad) || ""}
                      alt={safeText(item.ad?.title)}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-1">
                        {safeText(item.ad?.title)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Quantidade: {safeText(item.quantity)}
                      </p>
                      <p className="text-sm font-semibold text-primary">
                        {formatCurrency(getItemPriceReais(item) * (Number(item.quantity) || 1))}
                      </p>
                    </div>
                  </div>
                  {/* Offer details: same as cart page — product(s) offered + cash + total */}
                  {item.isOfferItem && (
                    <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-3 space-y-3 text-sm ml-7">
                      <h4 className="font-semibold text-foreground">Detalhes da Oferta:</h4>
                      {item.offerTradeInAds && item.offerTradeInAds.length > 0 && (
                        <>
                          <p className="text-muted-foreground font-medium">Produto(s) oferecido(s):</p>
                          <ul className="space-y-2">
                            {item.offerTradeInAds.map((t) => (
                              <li key={t.id} className="flex items-center gap-3 rounded-lg bg-background/50 p-2">
                                <AdImage
                                  src={t.image || undefined}
                                  alt={safeText(t.title)}
                                  className="w-12 h-12 object-cover rounded shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-foreground truncate">{safeText(t.title)}</p>
                                  <p className="text-primary font-medium">{formatCurrency(Number(t.value) || 0)}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Valor dos produtos:</span>
                            <span>{formatCurrency(item.offerTradeInAds.reduce((s, t) => s + (Number(t.value) || 0), 0))}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>Pagamento em dinheiro:</span>
                        <span>{formatCurrency(Number(item.offerPrice) || 0)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-primary pt-1 border-t border-border">
                        <span>Valor total da oferta:</span>
                        <span>
                          {formatCurrency(
                            (item.offerTradeInAds?.reduce((s, t) => s + (Number(t.value) || 0), 0) ?? 0) + (Number(item.offerPrice) || 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Method */}
          {selectedShipping && (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Forma de Envio</h3>
              </div>
              <div className="pl-7">
                {routeLabels && (
                  <div className="mb-3 rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Trajeto do envio</p>
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{safeText(routeLabels.origin)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="font-medium">{safeText(routeLabels.hub)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="font-medium">{safeText(routeLabels.destination)}</span>
                    </p>
                    {(selectedShipping.leg1_1 != null || selectedShipping.leg2 != null) && (
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {selectedShipping.leg1_1 != null && (
                          <span>Até o hub: {formatCurrency(selectedShipping.leg1_1)}</span>
                        )}
                        {selectedShipping.leg2 != null && (
                          <span>Hub até destino: {formatCurrency(selectedShipping.leg2)}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="font-medium">{safeText(selectedShipping.name)}</p>
                <p className="text-sm text-muted-foreground">
                  {safeText(selectedShipping.estimatedDays)}
                </p>
                <p className="text-sm font-semibold text-primary mt-1">
                  {formatCurrency(Number(selectedShipping.price) || 0)}
                </p>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="bg-card border-2 border-primary/20 rounded-lg p-6 space-y-4 shadow-md">
            <h2 className="font-semibold text-lg mb-4 text-primary">Resumo do Pedido</h2>
            
            <div className="space-y-3">
              {!isSwapValidate && (
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Subtotal dos Produtos</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
              )}
              {isSwapValidate && cashDiffCents > 0 && (
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Diferença em dinheiro (troca)</span>
                  <span className="font-medium">{formatCurrency(cashDiffCents / 100)}</span>
                </div>
              )}
              <div className="flex justify-between text-base">
                <span className="text-muted-foreground">Frete</span>
                <span className="font-medium">{formatCurrency(shipping)}</span>
              </div>
              {serverPlatformFeeCents != null && serverPlatformFeeCents > 0 && (
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Taxa de Proteção e Validação.</span>
                  <span className="font-medium">{formatCurrency(platformFeeReais)}</span>
                </div>
              )}
              {isSwapValidate && buyerHandlingCents > 0 && (
                <div className="flex justify-between text-base">
                  <span className="text-muted-foreground">Serviços de Manuseio</span>
                  <span className="font-medium">{formatCurrency(buyerHandlingCents / 100)}</span>
                </div>
              )}

              {/* Show interest if applicable (only when payment is credit with installments) */}
              {selectedPayment?.type === "credit" && hasInterest && (
                <div className="flex justify-between text-base bg-amber-50 dark:bg-amber-950/20 -mx-2 px-2 py-2 rounded">
                  <span className="text-amber-700 dark:text-amber-600 font-medium">
                    Juros ({selectedInstallments}x)
                  </span>
                  <span className="text-amber-700 dark:text-amber-600 font-semibold">
                    + {formatCurrency(totalWithInterest - total)}
                  </span>
                </div>
              )}
              
              <div className="border-t-2 border-primary/20 pt-4 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg font-semibold">
                    Total {hasInterest ? 'com Juros' : ''}
                  </span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCurrency(hasInterest ? totalWithInterest : total)}
                  </span>
                </div>
                
                {/* Show installment breakdown if applicable */}
                {selectedPayment?.type === "credit" && selectedInstallments > 1 && (
                  <div className="mt-3 p-3 bg-primary/5 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground font-medium">
                        Você pagará:
                      </span>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary">
                          {selectedInstallments}x de {formatCurrency(installmentAmount)}
                        </p>
                        {hasInterest ? (
                          <p className="text-xs text-amber-600 dark:text-amber-500 font-medium mt-1">
                            Com juros de {(interestRate * 100).toFixed(2).replace('.', ',')}%
                          </p>
                        ) : (
                          <p className="text-xs text-green-600 dark:text-green-500 font-medium mt-1">
                            Sem juros
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {validatePricingError != null && safeText(validatePricingError) !== "" && (
            <p className="text-sm text-destructive text-center mb-2" role="alert">
              {safeText(validatePricingError)}
            </p>
          )}
          {orderError != null && safeText(orderError) !== "" && (
            <p className="text-sm text-destructive text-center mb-2" role="alert">
              {safeText(orderError)}
            </p>
          )}
          {/* Confirm Button */}
          <Button
            onClick={onConfirmOrder}
            className="w-full"
            size="lg"
            disabled={
              isConfirming ||
              validatePricingError != null ||
              (!!getAccessToken() && serverPlatformFeeCents === null)
            }
          >
            {isConfirming ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              "Confirmar Pedido"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Ao confirmar, você concorda com nossos termos e condições
          </p>
        </div>
      </div>
    </div>
  );
}